// @ts-check
import {head, prop, find, propEq} from 'ramda'
import Debug from 'debug'
import { fetchAddressOptions } from '../address'
import { SCREENING } from '../../../component-templates/certification-badge/constants'
import confirmationDialogTemplate from "./confirmation-dialog/confirmation-dialog.html"
import "./confirmation-dialog/confirmation-dialog.css"
import { ConfirmationDialogController } from "./confirmation-dialog/confirmation-dialog.controller";
import { getSplitAttributes } from '../../../shared/split'

const debug = Debug('app:company:create:ctrl')

const DEFAULT_COMPANY_INFO = {
  avg_weekly_workers: 'N/A',
  temp_to_hire: 'N/A',
  markup: 20,
  vmarkup: null,
  vmarkupOt: null,
  vmarkupDt: null,
  vmarkupHosted: null,
  vmarkupHostedOt: null,
  vmarkupHostedDt: null,
  net_pay: 15,
  pricing_type: 1,
  refill_on: 0,
  beta_active: 1,
  signed_contract: 0,
  background_check_required: 0,
  background_check_active: false,
  drug_screen_required: 0,
  drug_screen_active: false,
}

const drugScreeningSettingName = 'drug_screen_required'
const SCREENING_DISABLED_VALUE = 0
const SCREENING_DEFAULT_ENABLED_VALUE = 1

export class EditCompanyController {
  /* @ngInject */
  constructor (Auth, user, companiesList, Companies, $mdDialog, $mdToast, admins, $scope, $q, companyInfo, companyCCIDsData, regions, $window) {
    this.Auth = Auth
    this.user = user
    this.accountManagers = admins
    this.$q = $q
    this.$window = $window
    this.Companies = Companies
    this.regions = regions
    this.$mdToast = $mdToast
    this.$scope = $scope
    this.searchText = ''
    this.selectedPlace = ''
    this.$mdDialog = $mdDialog
    $scope.logoURL = ''
    this.companiesList = companiesList
    this.SCREENING = SCREENING

    $scope.imageUpload = (elem) => {
      const reader = new FileReader()
      reader.onload = $scope.imageIsLoaded
      reader.readAsDataURL(elem.files[0])
    }

    $scope.imageIsLoaded = (e) => {
      $scope.logoURL = e.target.result
      this.photoChosen = true
      this.editPhoto = false
      $scope.$apply()
    }

    this.editPhoto = true
    this.photoChosen = false
    this.myCroppedImage = ''

    this.companyInfo = companyInfo || DEFAULT_COMPANY_INFO
    this.companyCCIDsData = companyCCIDsData || []
    // We are currently getting Cost Center ID1 from Payrol Portal service.
    // We are getting the array of CCIDs mappings, and take the 1st one, since all CCIDs1 are similar
    this.companyInfo.cost_center_id = this.companyCCIDsData.length ? this.companyCCIDsData[0].ccid1 : 'N/A'
    this.initialBusinessUnit = this.companyInfo.business_unit
    this.initialProductCode = this.companyInfo.product_code
    if (companyInfo) {
      this.selectedAM = find(propEq('id', companyInfo.account_manager))(this.accountManagers)
      this.searchText = this.getAddrString(companyInfo)
    }
    if (companyInfo) {
      this.selectedParentCompany = find(propEq('company_id', companyInfo.parent_company_id))(this.companiesList)
    }
    this.showHosted = false;
    this.pricingType = this.companyInfo.pricing_type;
    this.setBillerInfo(this.companyInfo.id);

    this.Auth.splitClient.ready().then(() =>  {
      // Company attributes are selected from the company that is being edited.
      const attributes = { ...getSplitAttributes(this.Auth), company_id: this.companyInfo.internal_id, company_name: this.companyInfo.name };
      this.drug_screening_p0_enabled = this.Auth.splitClient.getTreatment("drug_screening_p0", attributes) === "on"
      this.advanced_background_check_p0_enabled = this.Auth.splitClient.getTreatment("advanced_background_check_p0", attributes) === "on"
    })

    this.screeningFieldDropdownState = {
      background_check_required: this.companyInfo.background_check_required !== this.SCREENING.BACKGROUND.VALUES.NOT_ENABLED,
      drug_screen_required: this.companyInfo.drug_screen_required !== this.SCREENING.DRUG.VALUES.NOT_ENABLED,
    }

    this.initialScreeningRequired = {
      "drug_screen_required": this.companyInfo.drug_screen_required,
      "background_check_required": this.companyInfo.background_check_required,
    }
  }

  /**
   * @param settingRequiredKey {string} - either 'drug_screen_required' or 'background_check_required'.
   * @param firstWarningText {string} - Text that'll go on the first line of the confirmation dialog.
   * @param secondWarningText {string} - Text that'll go on the second line of the confirmation dialog.
   *
   * Opens up a confirmation dialog using the mdDialog service. If the user 'cancels', it'll set the initial
   * 'required' setting for either 'drug_screen' or 'background_check' back to the original value. If the user
   * 'removes' the setting, it'll set the 'required' setting to 0 (disabled).
   */
  openConfirmationDialog(settingRequiredKey, firstWarningText, secondWarningText) {
    this.$mdDialog.show({
      controller: ConfirmationDialogController,
      controllerAs: "$ctrl",
      template: confirmationDialogTemplate,
      clickOutsideToClose: true,
      multiple: true,
      locals: {
        firstWarningText,
        secondWarningText,
        resetScreeningRequiredValues: () => { // Clicked 'No, go back'
          this.companyInfo[settingRequiredKey] = this.initialScreeningRequired[settingRequiredKey]
          this.screeningFieldDropdownState[settingRequiredKey] = this.companyInfo[settingRequiredKey] !== SCREENING_DISABLED_VALUE
        },
        disableScreeningRequiredValues: () => { // Clicked 'Remove'
          this.companyInfo[settingRequiredKey] = SCREENING_DISABLED_VALUE
        }
      },
    })
  }

  checkDisableScreeningField(settingRequiredKey) {
    // Checkbox is 'checked' and dropdown list of radio buttons is visible.
    if (this.screeningFieldDropdownState[settingRequiredKey]) {
      this.companyInfo[settingRequiredKey] = SCREENING_DEFAULT_ENABLED_VALUE
    }
    // If (background_check/drug_screen)_required was enabled (value > 0) then they disabled, show confirmation modal.
    else if (this.initialScreeningRequired[settingRequiredKey]!== SCREENING_DISABLED_VALUE && !this.screeningFieldDropdownState[settingRequiredKey]) {
      // These are the warning messages that will be displayed in the confirmation dialog
      const firstWarningText = settingRequiredKey === drugScreeningSettingName ? 'Remove drug screening' : 'Remove advanced background'
      const secondWarningText = settingRequiredKey === drugScreeningSettingName ? 'requirement' : 'check requirement'
      this.openConfirmationDialog(settingRequiredKey, firstWarningText, secondWarningText)
    }
  }

  /**
   * @param settingRequiredKey {string} - is either 'drug_screen_required' or 'background_check_required'
   * ng-model sets values as strings for 'safety' even if the key-value types are correct. We need to explicitly convert
   * to an integer.
   */
  checkScreeningFieldSelection(settingRequiredKey) {
    this.companyInfo[settingRequiredKey] = parseInt(this.companyInfo[settingRequiredKey])
  }

  onHostedMarkup(active) {
    this.showHosted=active;
  }

  close () {
    this.$mdDialog.cancel()
  }

  editCompany () {
    if (this.editCompanyForm.$invalid) {
      debug('form not valid!')
      return
    }

    if (this.myCroppedImage !== '') {
      var data = this.myCroppedImage.substring(this.myCroppedImage.indexOf(',') + 1)
      this.Companies.uploadLogo({
        logo_base64: data
      })
      .then((res) => {
        this.companyInfo.logo_id = res.internalId || res.id
        this.updateCompany()
      })
      .catch((err) => {
        if (!err) return
        this.$mdToast.showSimple('Failed To Upload Logo')
      })
    } else {
      this.updateCompany()
    }
  }

  migrateV3Company () {
    // migrate schedules and positions from v2
    this.clickBlocker = true
    const confirm = this.$mdDialog.confirm()
      .title('Confirm migration')
      .multiple(true)
      .textContent(`Are you sure you want to migrate ${this.companyInfo.name}? This will convert all their job posts to new schedules and they will need to be renamed from defaults (schedule #1, #2)`)
      .ariaLabel('Confirmigration')
      .ok('Migrate')
      .cancel('Cancel')
    return this.$mdDialog.show(confirm)
    .then(
      () => {
        this.Companies.migrateV3Company([this.companyInfo.internal_id])
        this.$mdToast.showSimple('Success Mirating Company')
      },
      () => {
        this.clickBlocker = false
        this.$mdToast.showSimple('error migrating company, please log into V3 again')
        return
      }
    )
  }

  fieldChanged (initialValue, currentValue) {
    return initialValue !== currentValue;
  }

  updateCompany () {
    this.companyInfo.hubspot_id = this.companyInfo.hubspot_id !== '' ? this.companyInfo.hubspot_id : null
    this.companyInfo.cost_center_id = this.companyInfo.cost_center_id !== '' ? this.companyInfo.cost_center_id : null
    this.companyInfo.business_unit = this.companyInfo.business_unit !== '' ? this.companyInfo.business_unit : null
    this.companyInfo.product_code = this.companyInfo.product_code !== '' ? this.companyInfo.product_code : null

    //pricing_type 8 means the user selected variable markup rate so if not selected then set variable markups to 0
    //if 8 selected then set pricing_type to 0 since that pricing type is used to designate hourly_markup (we only use 8 in ui not backend)
    //
    this.companyInfo.pricing_type = this.pricingType;

    if ((!this.showHosted) || (this.companyInfo.pricing_type != 8)) {
      this.companyInfo.vmarkupHosted = 0
      this.companyInfo.vmarkupHostedDt = 0
      this.companyInfo.vmarkupHostedOt = 0
    }
    if (this.companyInfo.pricing_type != 8) {
      this.companyInfo.vmarkup = 0
      this.companyInfo.vmarkupDt = 0
      this.companyInfo.vmarkupOt = 0
    }
    else {
      this.companyInfo.pricing_type = 0;
      this.companyInfo.markup = this.companyInfo.vmarkup;
    }

    this.Companies.updateCompany({
      companyInfo: this.companyInfo,
    })
      .then((res) => {
        // We create Cost Center1 at Payrol Portal service, only if Bussiness Unit or Product Code fields have changed
        if (this.fieldChanged(this.initialBusinessUnit, this.companyInfo.business_unit) ||
            this.fieldChanged(this.initialProductCode, this.companyInfo.product_code)) {
              return this.Companies.createCostCenter1(
                this.companyInfo.internal_id,
                this.companyInfo.business_unit,
                this.companyInfo.region_id,
                this.companyInfo.product_code,
                this.companyInfo.name
              )
            }
      })
      .then((res) => {
        this.$mdToast.showSimple('Successfully Updated Company')
        this.$mdDialog.hide(this.companyInfo)
      })
      .catch((err) => {
        if (!err) return
        debug(err)
        this.$mdToast.showSimple('Failed To Update Company')
      })
  }

  // Return default value for address input after selecting an exiting template.
  getAddrString (companyInfo) {
    return `${companyInfo.address}, ${companyInfo.city}, ${companyInfo.state} ${companyInfo.zipcode}, United States`
  }

  queryAddress (input) {
    const def = this.$q.defer();
    this.AutocompleteService =
      new this.$window.google.maps.places.AutocompleteService();
    this.PlacesService = new this.$window.google.maps.places.PlacesService(
      document.getElementById("places-div")
    );

    fetchAddressOptions(input, def, this.AutocompleteService, this.PlacesService)

    return def.promise
  }

  handleSelectAddress() {
    const def = this.$q.defer()
    if (!this.selectedPlace) {
      this.editCompanyForm.address.$error['md-require-match'] = true
      this.editCompanyForm.$invalid = true
      return def.resolve()
    } else {
      this.editCompanyForm.address.$error = {}
      this.editCompanyForm.$invalid = false;
    }

    let streetNumber;
    let streetName;
    let country;

    this.selectedPlace.address_components.forEach((x) => {
      const type = head(prop('types', x))
      if (type === 'street_number') streetNumber = x.long_name
      if (type === 'route') streetName = x.long_name
      if (type === 'locality') this.companyInfo.city = x.long_name
      if (type === 'country') country = x.long_name
      if (type === 'administrative_area_level_1') this.companyInfo.state = x.short_name
      if (type === 'administrative_area_level_2') this.companyInfo.county = x.long_name
      if (type === 'street_number') this.companyInfo.street_number = x.long_name
      if (type === 'postal_code') this.companyInfo.zipcode = x.long_name
    });


    this.companyInfo.address = !!streetNumber ? `${streetNumber} ${streetName}` : this.companyInfo.street
    this.companyInfo.address_latitude = this.selectedPlace.geometry.location.lat()
    this.companyInfo.address_longitude = this.selectedPlace.geometry.location.lng()

    if(!streetNumber || !this.companyInfo.zipcode) {
      this.editCompanyForm.address.$error['md-invalid-address'] = true
      this.editCompanyForm.$invalid = true
    }

    return def.resolve()
  }

  onRegionChange (regionId) {
    const selectedRegion = this.regions.find(r => r.id === regionId)

    if (selectedRegion && selectedRegion.default_business_unit) {
      this.companyInfo.business_unit = selectedRegion.default_business_unit
    }
  }
  setBillerInfo(id) {
    this.Companies.fetchBillerPlans(id)
      .then((plans) => {
        // Set to null so ui displays placeholders
        this.companyInfo.vmarkup = null;
        this.companyInfo.vmarkupOt = null;
        this.companyInfo.vmarkupDt = null;
        this.companyInfo.vmarkupHosted = null;
        this.companyInfo.vmarkupHostedOt = null;
        this.companyInfo.vmarkupHostedDt = null;

        // Get only plans with member_type === CREW_MEMBER, not HOSTED_MEMBER
        const crewMemberPlans = plans.filter(r => r.member_type === 'CREW_MEMBER');
        // The last plan in the array should be the current plan.
        const standardPlan = crewMemberPlans[crewMemberPlans.length - 1];
        // First find crew member plan (regular) rates. if plan type is hourly_markup we need to figure out if we should select standard markup or variable markup
        // If overtime or double time is set that means they entered variable markups so set pricing type to 8 so ui selects that radio button
        // If not set then set pricing_type to 0 so 'Standard markup' is selected in ui
        if (standardPlan) {
          if (standardPlan.plan_type === 'BILL_RATE') {
            this.companyInfo.markup = standardPlan.value;
            this.companyInfo.pricing_type = 1;
            this.pricingType = 1;
          }
          else if (standardPlan.plan_type === 'FLAT_DAILY_FEE') {
            this.companyInfo.markup = standardPlan.value;
            this.companyInfo.pricing_type = 4;
            this.pricingType = 4;
          }
          else {
            if (((standardPlan.value_overtime) && (standardPlan.value_overtime !== 0)) ||
                ((standardPlan.value_doubletime) && (standardPlan.value_doubletime !== 0))) {
                  this.companyInfo.vmarkup = standardPlan.value;
                  this.companyInfo.vmarkupOt = standardPlan.value_overtime;
                  this.companyInfo.vmarkupDt = standardPlan.value_doubletime;
                  this.companyInfo.pricing_type = 8;
                  this.pricingType = 8;
            }
            else {
              this.companyInfo.markup = standardPlan.value;
              this.companyInfo.pricing_type = 0;
              this.pricingType = 0;
            }
          }
        }
        // Now check if there is a hosted plan for this company.  if so and not empty then set values and select the correct radio button (pricing_type)
        const hostedPlans = plans.filter(r => r.member_type === 'HOSTED_MEMBER');
        // Select latest hosted plan
        const currentHostedPlan = hostedPlans[hostedPlans.length - 1];
        if (currentHostedPlan && currentHostedPlan.value && currentHostedPlan.value !== 0) {
          this.companyInfo.vmarkupHosted = currentHostedPlan.value;
          this.companyInfo.vmarkupHostedOt = currentHostedPlan.value_overtime;
          this.companyInfo.vmarkupHostedDt = currentHostedPlan.value_doubletime;
          this.companyInfo.pricing_type = 8;
          this.pricingType = 8;
          this.showHosted = true;
        }
      })
      .catch((err) => {
        if (!err) return
        debug(err)
        this.$mdToast.showSimple('Failed To load markup rates')
      })
  }
}
